import { configureStore } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import createWebStorage from "redux-persist/lib/storage/createWebStorage";
import { WebStorage } from "redux-persist/lib/types";
import { createTransform } from "redux-persist";

import middleware from "../middleware";
import { rootReducer } from "../reducers"; // Ensure this path is correct

/**
 * Strip Mongoose Document internals (produced when backend forgot .lean()).
 * Safe to call on any value: plain objects pass through unchanged.
 */
function stripMongooseDoc(obj: any): any {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  if ("$__" in obj || "_doc" in obj) {
    // Return only the plain _doc contents, recursively cleaned
    const source = obj._doc ?? obj;
    const cleaned: Record<string, any> = {};
    for (const key of Object.keys(source)) {
      if (key === "$__" || key === "$isNew" || key === "_doc") continue;
      cleaned[key] = stripMongooseDoc(source[key]);
    }
    return cleaned;
  }
  const result: Record<string, any> = {};
  for (const key of Object.keys(obj)) {
    result[key] = stripMongooseDoc(obj[key]);
  }
  return result;
}

/**
 * Reduce tenantSubscription to only what is needed for menu-visibility checks.
 * Strips all PII (user name/email/company) and commercial data (pricing, limits)
 * before writing to localStorage.
 */
function minifyTenantSubscription(sub: any): any {
  if (!sub) return null;
  const cleaned = stripMongooseDoc(sub);
  const currentSub = cleaned?.currentSub;
  if (!currentSub) return null;
  return {
    currentSub: {
      active: currentSub.active === true,
      plan: {
        allowedPages: Array.isArray(currentSub.plan?.allowedPages)
          ? currentSub.plan.allowedPages
          : Array.isArray(currentSub.planId?.allowedPages)
            ? currentSub.planId.allowedPages
            : Array.isArray(currentSub.allowedPagesAtPurchase)
              ? currentSub.allowedPagesAtPurchase
              : [],
      },
    },
  };
}

const safeTermsTransform = createTransform(
  // inbound: save to storage — strip PII/pricing, keep only what menus need
  (inboundState: any) => ({
    terms: {
      data: inboundState?.terms?.data || {
        serviceUser: "",
        staff: "",
        care: "",
      },
      loading: false,
      error: null,
    },
    tenantSubscription: minifyTenantSubscription(
      inboundState?.tenantSubscription
    ),
    tenantSubscriptionLoaded: inboundState?.tenantSubscriptionLoaded === true,
  }),
  // outbound: read from storage — pass through (already minified on write)
  (outboundState: any) => ({
    terms: {
      data: outboundState?.terms?.data || {
        serviceUser: "",
        staff: "",
        care: "",
      },
      loading: false,
      error: null,
    },
    tenantSubscription: minifyTenantSubscription(
      outboundState?.tenantSubscription
    ),
    tenantSubscriptionLoaded: outboundState?.tenantSubscriptionLoaded === true,
  }),
  { whitelist: ["terms"] }
);

export function createPersistStorage(): WebStorage {
  const isServer = typeof window === "undefined";

  if (isServer) {
    return {
      getItem() {
        return Promise.resolve(null);
      },
      setItem() {
        return Promise.resolve();
      },
      removeItem() {
        return Promise.resolve();
      },
    };
  }

  return createWebStorage("local");
}

const persistConfig = {
  key: "root",
  storage: createPersistStorage(),
  // Persist only low-risk UI state. Do NOT persist auth/session or PII-rich
  // entities like user/service users in browser storage.
  whitelist: ["terms"],
  transforms: [safeTermsTransform],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }).prepend(middleware),
});
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const persistor = persistStore(store);
